import React, { useState, useMemo } from 'react';
import { usePendingJobs } from '../../hooks/useAdmin';
import { getJobStatusLabel, getJobStatusColor } from '../../types/job';
import TableHeader from '../../components/common/TableHeader';
import './AdminJobs.css';

const AdminPendingJobs = () => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  const { data: jobs, isLoading } = usePendingJobs();

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const sortedJobs = useMemo(() => {
    if (!jobs || !sortConfig.key) return jobs;
    
    return [...jobs].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'job_role':
          aVal = (a.job_role?.job_role_name || a.job_role_name || a.job_role || '').toLowerCase();
          bVal = (b.job_role?.job_role_name || b.job_role_name || b.job_role || '').toLowerCase();
          break;
        case 'employer':
          aVal = (a.employer_email || a.employer?.email || a.employer_name || '').toLowerCase();
          bVal = (b.employer_email || b.employer?.email || b.employer_name || '').toLowerCase();
          break;
        case 'date':
          aVal = (a.start_date || a.job_date || a.date) ? new Date(a.start_date || a.job_date || a.date).getTime() : 0;
          bVal = (b.start_date || b.job_date || b.date) ? new Date(b.start_date || b.job_date || b.date).getTime() : 0;
          break;
        case 'hours':
          aVal = a.total_hours || a.hours || 0;
          bVal = b.total_hours || b.hours || 0;
          break;
        case 'status':
          aVal = a.status?.toLowerCase() || '';
          bVal = b.status?.toLowerCase() || '';
          break;
        case 'created':
          aVal = (a.created_at || a.date_created) ? new Date(a.created_at || a.date_created).getTime() : 0;
          bVal = (b.created_at || b.date_created) ? new Date(b.created_at || b.date_created).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [jobs, sortConfig]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  if (isLoading) {
    return (
      <div className="admin-jobs__loading">
        <div className="admin-jobs__spinner"></div>
        <p>Loading pending jobs...</p>
      </div>
    );
  }

  return (
    <div className="admin-jobs">
      <div className="admin-jobs__header">
        <h1 className="admin-jobs__title">Pending Jobs</h1>
      </div>

      <div className="admin-jobs__table-container">
        <table className="admin-jobs__table">
          <thead>
            <tr>
              <TableHeader 
                sortKey="job_role" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Job Role
              </TableHeader>
              <TableHeader 
                sortKey="employer" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Employer
              </TableHeader>
              <TableHeader 
                sortKey="date" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Date
              </TableHeader>
              <TableHeader 
                sortKey="hours" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Hours
              </TableHeader>
              <TableHeader 
                sortKey="status" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Status
              </TableHeader>
              <TableHeader 
                sortKey="created" 
                currentSortKey={sortConfig.key} 
                sortDirection={sortConfig.direction}
                onSort={handleSort}
              >
                Created
              </TableHeader>
            </tr>
          </thead>
          <tbody>
            {sortedJobs && sortedJobs.length > 0 ? (
              sortedJobs.map((job) => (
                <tr key={job.id}>
                  <td>{job.job_role?.job_role_name || job.job_role_name || job.job_role || 'N/A'}</td>
                  <td>{job.employer_email || job.employer?.email || job.employer_name || 'N/A'}</td>
                  <td>{formatDate(job.start_date || job.job_date || job.date)}</td>
                  <td>{job.total_hours || job.hours || 'N/A'} {job.total_hours || job.hours ? 'hours' : ''}</td>
                  <td>
                    <span
                      className="admin-jobs__status"
                      style={{ backgroundColor: getJobStatusColor(job.status) }}
                    >
                      {getJobStatusLabel(job.status)}
                    </span>
                  </td>
                  <td>{formatDate(job.created_at || job.date_created)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="admin-jobs__empty">No pending jobs found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPendingJobs;

